/**
 * Splits an email HTML string into the latest message and the trimmed content
 * (signature + quoted history), which the UI folds away behind a single
 * "Show trimmed content" toggle.
 *
 * Detects common email quoting patterns:
 * - Gmail: <div class="gmail_quote gmail_quote_container"> or <div class="gmail_quote">
 * - Outlook: <div id="divRplyFwdMsg"> or <div id="appendonsend">
 * - Generic: <blockquote> elements used for quoting
 * - "On ... wrote:" attribution lines followed by blockquotes
 *
 * Signatures are folded into the same trimmed zone: when a signature block
 * (Gmail signature container or an RFC 3676 `--` delimiter line) appears before
 * the quoted history, the fold boundary moves up to the signature so both are
 * hidden together. The cut is done with a DOM Range from the boundary through
 * the end of the body, so it works even when the signature and the quote sit at
 * different nesting depths.
 */

export function addTargetBlankToLinks(html: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");

	doc.querySelectorAll("a[href]").forEach((anchor) => {
		const href = anchor.getAttribute("href") ?? "";

		if (!href.startsWith("mailto:") && !href.startsWith("tel:")) {
			anchor.setAttribute("target", "_blank");
			anchor.setAttribute("rel", "noopener noreferrer");
		}
	});

	return doc.body.innerHTML;
}

export function splitEmailHistory(html: string): {
	latestMessage: string;
	history: string | null;
} {
	if (!html) {
		return { latestMessage: "", history: null };
	}

	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const body = doc.body;

		let historyElement = findHistoryElement(body);

		if (historyElement) {
			historyElement = includePrecedingAttribution(historyElement);
		}

		const signatureElement = findSignatureElement(body);
		// Fold from whichever boundary comes first in document order so the
		// signature and the quoted history collapse into one trimmed zone.
		const boundary = earliestInDocument(signatureElement, historyElement);

		if (!boundary || !body.lastChild) {
			return { latestMessage: addTargetBlankToLinks(html), history: null };
		}

		// Select everything from the boundary through the end of the body. A Range
		// keeps preceding content and partially-selected ancestors intact, so the
		// message text before a nested signature survives while the trailing zone
		// is removed.
		const range = doc.createRange();

		range.setStartBefore(boundary);
		range.setEndAfter(body.lastChild);

		const holder = doc.createElement("div");

		holder.appendChild(range.cloneContents());
		const history = holder.innerHTML;

		range.deleteContents();
		const latestMessage = addTargetBlankToLinks(body.innerHTML.trim());

		// Only return trimmed content if there's actual content in the latest message.
		if (!latestMessage) {
			return { latestMessage: addTargetBlankToLinks(html), history: null };
		}

		return { latestMessage, history: addTargetBlankToLinks(history) };
	} catch {
		// If parsing fails for any reason, return the original HTML.
		return { latestMessage: html, history: null };
	}
}

/**
 * Finds the history element in the parsed email body.
 */
function findHistoryElement(body: HTMLElement): Element | null {
	// Patterns to detect quoted email history (ordered by specificity)
	const historySelectors = [
		// Gmail quote container (most specific)
		".gmail_quote_container",
		// Gmail quote div (not blockquote)
		"div.gmail_quote",
		// Outlook reply/forward marker
		"#divRplyFwdMsg",
		// Outlook appendonsend
		"#appendonsend",
		// Yahoo mail quote
		".yahoo_quoted",
		// Generic blockquote at top level of body
		":scope > blockquote",
	];

	for (const selector of historySelectors) {
		const element = body.querySelector(selector);

		if (element) {
			return element;
		}
	}

	// Fallback: look for "On ... wrote:" pattern followed by blockquote
	const allElements = body.querySelectorAll("*");

	for (let i = 0; i < allElements.length; i++) {
		const el = allElements[i];

		if (
			el.classList.contains("gmail_attr") ||
			(el.textContent?.match(/^On .+ wrote:$/m) && el.nextElementSibling?.tagName === "BLOCKQUOTE")
		) {
			return el;
		}
	}

	return null;
}

/**
 * Finds the signature block in the parsed email body, so it can be folded into
 * the trimmed zone together with the quoted history. Conservative on purpose —
 * only clear signature markers count, to avoid trimming real message content.
 */
function findSignatureElement(body: HTMLElement): Element | null {
	// Explicit signature containers (Gmail).
	const signatureContainer = body.querySelector(
		'.gmail_signature, [data-smartmail="gmail_signature"]'
	);

	if (signatureContainer) {
		return signatureContainer;
	}

	// RFC 3676 "-- " delimiter rendered as its own block element.
	const blocks = body.querySelectorAll("div, p");

	for (const block of blocks) {
		if (block.textContent && /^--\s*$/.test(block.textContent.trim())) {
			return block;
		}
	}

	return null;
}

/**
 * If the boundary is directly preceded by an "On … wrote:" attribution line,
 * move the boundary up to include it, so the attribution folds with the quote.
 */
function includePrecedingAttribution(element: Element): Element {
	const prev = element.previousElementSibling;

	if (
		prev &&
		(prev.classList.contains("gmail_attr") || /^On .+ wrote:$/m.test(prev.textContent ?? ""))
	) {
		return prev;
	}

	return element;
}

/**
 * Returns whichever of the two elements comes first in document order (ignoring
 * nulls), so folding starts at the earliest trimmed boundary.
 */
function earliestInDocument(a: Element | null, b: Element | null): Element | null {
	if (!a) {
		return b;
	}

	if (!b) {
		return a;
	}

	// DOCUMENT_POSITION_FOLLOWING (4) means b follows a → a is earlier.
	return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? a : b;
}
